import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router';

import { menus } from '@/constants/menus';
import { useCompanyStore } from '@/stores/company.store';
import { useFilterStore } from '@/stores/filter.store';
import { forgetListFilters } from '@/hooks/use-url-filters';

/** Where a switch lands when nothing of the current route survives it. */
const LANDING_ROUTE = '/planner';

/** The segment every create and edit screen in the CMS sits behind. */
const FORM_SEGMENT = 'form';

/**
 * The pages an operator can actually navigate to, longest first.
 *
 * Leaves only: `/schedule`, `/pricing`, `/inventory` and friends are group
 * headers in the sidebar with no screen behind them, and falling back to one
 * would land on the not-found page. Taking the list from the menu rather than
 * from the route table keeps this to real, reachable destinations — the route
 * table also holds `/inventory/smart-suggestion/filters`, which is a path
 * prefix and not a page.
 */
const SECTION_ROUTES = menus
  .flatMap(menu => (menu.children?.length ? menu.children : [menu]))
  .map(menu => menu.route)
  .sort((left, right) => right.length - left.length);

/** Whether the path names one particular record, rather than a screen. */
function namesARecord(segments: string[]): boolean {
  const form = segments.indexOf(FORM_SEGMENT);

  // `/bookings/form` is the create screen — a form with no record in it, and
  // nothing that stops meaning something when the tenant changes.
  return form !== -1 && form < segments.length - 1;
}

/**
 * Where the operator should be standing once the tenant has changed.
 *
 * A switch changes which company the screen is about, not which screen the
 * operator wanted to be on, so the answer is "here" wherever "here" still
 * means something. Two parts of the URL do not survive.
 *
 * The search string, always — dropped by the caller. Half the CMS keeps a
 * tenant-owned id there: `?location`, `?resource`, `?activity`, `?fnb`,
 * `?booking_id`, `?location_id`. None of them name anything under the new
 * company, and once they are gone the screens fall back to the new tenant's
 * own default branch through `useDefaultLocation`, on the remount that
 * `CompanyScopedOutlet` performs anyway.
 *
 * A record named in the path. `/bookings/form/<id>` is one company's booking;
 * under another it is a not-found at best. Those fall back to the section the
 * record belongs to — the list the operator would have reached it from — which
 * keeps them in the same part of the CMS instead of throwing them to the
 * planner.
 *
 * Every other route keeps its path untouched: lists, dashboards, settings,
 * the planner, and create forms.
 */
export function companySwitchDestination(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);

  if (!namesARecord(segments)) return pathname || LANDING_ROUTE;

  return (
    SECTION_ROUTES.find(
      route => pathname === route || pathname.startsWith(`${route}/`)
    ) ?? LANDING_ROUTE
  );
}

/**
 * Drops everything the previous tenant left behind.
 *
 * The company reaches the API through a request interceptor, below the query
 * cache, so it never becomes part of a query key: of the roughly 545 keys in
 * the CMS only a handful name a company at all. Cached rows are therefore
 * indistinguishable between tenants, and anything already fetched would be
 * served straight back under the new tenant's name.
 *
 * Cancelling comes first. `clear` empties the cache but does not stop a
 * request that is already on its way, and a response landing afterwards would
 * repopulate the cache it was meant to empty — with the previous tenant's
 * rows.
 *
 * Filters go too: `location` holds a whole branch belonging to the previous
 * company, which does not exist under the new one.
 *
 * Navigating last, and only when the URL itself has to change — see
 * `companySwitchDestination`. Standing still is safe: the screen is remounted
 * on the scope either way, by `CompanyScopedOutlet`.
 */
export async function resetForCompanySwitch(
  queryClient: QueryClient,
  navigate: NavigateFunction,
  from: { pathname: string; search: string }
): Promise<void> {
  await queryClient.cancelQueries();
  queryClient.clear();
  useFilterStore.getState().reset();
  forgetListFilters();

  // Only now: the screens are keyed on this, so bumping it any earlier would
  // remount them against a cache the lines above are still emptying.
  useCompanyStore.getState().beginNewScope();

  const destination = companySwitchDestination(from.pathname);

  // The URL already says what it should. Navigating anyway would push a
  // duplicate history entry, leaving Back doing nothing visible.
  if (destination === from.pathname && !from.search) return;

  // `replace`, not push: the entry being left behind names a record or a
  // branch of the previous tenant, and Back must not return the operator to it
  // under the new company.
  navigate(destination, { replace: true });
}

/**
 * Runs the teardown when the operator switches tenant.
 *
 * Only a real switch counts. The first selection is the sidebar picking a
 * company on a fresh login, and re-picking the one already active is a no-op —
 * neither had another tenant's data on screen to clear, and neither should
 * move the operator off the page they are reading.
 */
export function useCompanySwitch(): void {
  const companyId = useCompanyStore(state => state.company?.id);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const previousCompanyId = useRef(companyId);

  /**
   * Where the operator is standing, held in a ref rather than in the effect's
   * dependencies: the route changes far more often than the tenant does, and
   * the effect has no business re-running for it. Read inside the effect, so
   * it is always the route the switch actually happened on.
   */
  const here = useRef(location);
  here.current = location;

  useEffect(() => {
    const previous = previousCompanyId.current;
    previousCompanyId.current = companyId;

    if (!previous || previous === companyId) return;

    void resetForCompanySwitch(queryClient, navigate, here.current);
  }, [companyId, queryClient, navigate]);
}
